// Historial de comunicaciones (correos masivos).
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { App, Card, Dropdown, Empty, Input, Progress, Select, Table, Tag } from 'antd';
import { MoreOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import Comunicaciones, { mensajeError } from '../../../../service/Comunicaciones';
import { EncabezadoAdmin, claseBtnPrimario } from '../../../Ui/Admin/PiezasAdmin';
import { useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can } from '../../../../config/rbac';
import { ESTADOS, PLANTILLAS, fechaHora } from './textos';

const OPCIONES_ESTADO = [
  { value: 'todos', label: 'Todos los estados' },
  ...Object.entries(ESTADOS).map(([value, { label }]) => ({ value, label })),
];

export default function ComunicacionesLista() {
  const navigate = useNavigate();
  const notify = useNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();
  const puedeEditar = can(user, 'comunicaciones', 'editar');

  const [params, setParams] = useSearchParams();
  const estado = params.get('estado') || 'todos';
  const q = params.get('q') || '';
  const page = Number(params.get('page') || 1);

  const [busqueda, setBusqueda] = useState(q);
  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);

  const cambiar = (cambios) => {
    const p = new URLSearchParams(params);
    Object.entries(cambios).forEach(([k, v]) => (v && v !== 'todos' ? p.set(k, v) : p.delete(k)));
    setParams(p, { replace: true });
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await Comunicaciones.listar({ estado, q, page, limit: 20 });
      setFilas(res?.data || []);
      setTotal(res?.pagination?.total || 0);
    } catch (e) {
      notify.error(mensajeError(e, 'No se pudo cargar el historial'));
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, q, page]);

  useEffect(() => { cargar(); }, [cargar]);

  const borrar = (fila) =>
    modal.confirm({
      title: '¿Borrar este borrador?',
      content: fila.asunto,
      okText: 'Borrar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await Comunicaciones.eliminar(fila._id);
          notify.success('Borrador eliminado');
          cargar();
        } catch (e) {
          notify.error(mensajeError(e));
        }
      },
    });

  const columnas = [
    {
      title: 'Asunto',
      dataIndex: 'asunto',
      render: (asunto, f) => (
        <div>
          <Link to={`/admin/comunicaciones/${f._id}`} className="font-semibold text-gray-900 hover:text-primary">{asunto}</Link>
          <div className="text-xs text-gray-500">{PLANTILLAS[f.plantilla]?.label || f.plantilla}</div>
        </div>
      ),
    },
    { title: 'Público', dataIndex: 'publico', render: (v) => <span className="text-sm text-gray-700">{v}</span> },
    {
      title: 'Entrega',
      key: 'entrega',
      width: 210,
      render: (_, f) => {
        if (f.estado === 'borrador') return <span className="text-gray-400 text-sm">—</span>;
        const tot = f.totalDestinatarios || 0;
        const pct = tot ? Math.round((f.enviados / tot) * 100) : 0;
        const sin = (f.fallidos || 0) + (f.omitidos || 0);
        return (
          <div>
            <Progress percent={pct} size="small" showInfo={false} status={f.estado === 'fallida' ? 'exception' : undefined} />
            <div className="text-xs text-gray-500">
              {f.enviados.toLocaleString('es-CL')} de {tot.toLocaleString('es-CL')}
              {sin > 0 && ` · ${sin} sin entregar`}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 120,
      render: (e) => <Tag color={ESTADOS[e]?.color}>{ESTADOS[e]?.label || e}</Tag>,
    },
    {
      title: 'Creada',
      dataIndex: 'createdAt',
      width: 170,
      render: (v, f) => (
        <div className="text-sm">
          {fechaHora(v)}
          <div className="text-xs text-gray-500">{f.creadoPor}</div>
        </div>
      ),
    },
    {
      key: 'acciones',
      width: 56,
      render: (_, f) => {
        const items = [{ key: 'ver', label: 'Ver', onClick: () => navigate(`/admin/comunicaciones/${f._id}`) }];
        if (puedeEditar) {
          if (f.estado === 'borrador') items.push({ key: 'editar', label: 'Editar', onClick: () => navigate(`/admin/comunicaciones/${f._id}/editar`) });
          items.push({ key: 'dup', label: 'Duplicar', onClick: () => navigate(`/admin/comunicaciones/redactar?desde=${f._id}`) });
          if (f.estado === 'borrador') items.push({ key: 'borrar', label: 'Borrar', danger: true, onClick: () => borrar(f) });
        }
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <button type="button" aria-label="Acciones" className="p-2 rounded-full hover:bg-gray-100"><MoreOutlined /></button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-4 md:p-6">
      <EncabezadoAdmin titulo="Correos" destacado="masivos" subtitulo="Avisos por correo a inscritos, matriculados y clientes.">
        {puedeEditar && (
          <button type="button" className={claseBtnPrimario} onClick={() => navigate('/admin/comunicaciones/redactar')}>
            <PlusOutlined /> Redactar
          </button>
        )}
      </EncabezadoAdmin>

      <Card size="small" className="mb-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar por asunto"
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              if (!e.target.value) cambiar({ q: '', page: '' });
            }}
            onPressEnter={() => cambiar({ q: busqueda.trim(), page: '' })}
            className="sm:max-w-sm"
          />
          <Select value={estado} options={OPCIONES_ESTADO} onChange={(v) => cambiar({ estado: v, page: '' })} className="sm:w-56" />
        </div>
      </Card>

      <Card>
        <Table
          rowKey="_id"
          columns={columnas}
          dataSource={filas}
          loading={cargando}
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <Empty description={q || estado !== 'todos' ? 'Nada coincide con el filtro' : 'Aún no se ha enviado ningún correo'}>
                {puedeEditar && !q && estado === 'todos' && (
                  <button type="button" className={claseBtnPrimario} onClick={() => navigate('/admin/comunicaciones/redactar')}>
                    <PlusOutlined /> Redactar el primero
                  </button>
                )}
              </Empty>
            ),
          }}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            showSizeChanger: false,
            showTotal: (t) => `${t} comunicaciones`,
            onChange: (p) => cambiar({ page: p > 1 ? String(p) : '' }),
          }}
        />
      </Card>
    </div>
  );
}
